import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import Modal from "react-modal";
import moment from "moment-timezone";
import { selectAuth } from "../../components/auth/auth.selector";
import HelmetWrapper from "../../HelmetWrapper";
import PageHeadingNew from "../../components/common-components/custom-comps/PageHeadingNew";
import FilterContainer from "../../components/common-components/filters/filter-container.jsx";
import DateRangerFilter from "../../components/common-components/filters/date-ranger-filter.jsx";
import GymFilter from "../../components/common-components/filters/gym-filter.jsx";
import ModalHeadAndClose from "../../components/payouts-wtf/common/ModalHeadAndClose";
import { BASE_URL } from "../../data/BaseUrl";
import { timeSlotsQuarter } from "../../components/common-data/DropdowData";
import {
  VisitorsIcon,
  GymsIcon,
  ClockIcon,
  TrendingIcon,
  ConvertedIcon,
  PendingIcon,
  VisitDoneIcon,
  ChevronRightIcon,
  TimeSlotIcon,
  PersonIcon,
} from "../../components/analytics/GymVisitIcons";
import "../../styles/analytics/gym-visit-analytics.css";
import "../../styles/member-report/member-report.css";

const GymVisitAnalytics = () => {
  const authData = useSelector(selectAuth);
  const userInfoData = authData?.user || {};
  const isAdmin = userInfoData?.account_type === "admin";

  // Get current date info
  const currentMoment = moment().tz("Asia/Kolkata");
  const currentDate = currentMoment.format("YYYY-MM-DD");

  // State
  const [loading, setLoading] = useState(false);
  const [leadsData, setLeadsData] = useState([]);
  const [leadValues, setLeadValues] = useState(null); // Store API leadValues directly
  const [selectedGym, setSelectedGym] = useState(null);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [selectedFollowupTime, setSelectedFollowupTime] = useState([]);

  // Schedule stats state
  const [scheduleStats, setScheduleStats] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGymData, setSelectedGymData] = useState(null);


  // Handle date range change
  const handleDateChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setSelectedFollowupTime([]);
  };

  // Handle time slot selection
  const handleSlotClick = (slot) => {
    const isSelected = selectedFollowupTime.some(
      (selected) => selected.value === slot.value
    );

    if (isSelected) {
      setSelectedFollowupTime(
        selectedFollowupTime.filter((selected) => selected.value !== slot.value)
      );
    } else {
      setSelectedFollowupTime([...selectedFollowupTime, slot]);
    }
  };

  // Fetch leads data
  const fetchLeadsData = useCallback(async () => {
    if (!userInfoData?.token) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: "1",
        limit: "999",
        source_name: "gym_visit",
        followup_date_start: startDate,
        followup_date_end: endDate,
      });

      if (selectedGym?.value) {
        params.append("gym_id", selectedGym.value);
      }

      // Build followup time filter string (Q1-Q5 quarters)
      const timeFilter = selectedFollowupTime.length > 0
        ? selectedFollowupTime.map((slot) => slot.value).join(",")
        : "";
      if (timeFilter) {
        params.append("followup_time", timeFilter);
      }

      const response = await fetch(`${BASE_URL}/leads/v2?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userInfoData.token}`,
        },
      });

      const result = await response.json();
      if (result?.status && result?.data) {
        setLeadsData(result.data);
        // Store leadValues from API directly
        setLeadValues(result.leadValues || null);
      } else {
        setLeadsData([]);
        setLeadValues(null);
      }
    } catch (error) {
      console.error("Error fetching gym visit data:", error);
      setLeadsData([]);
    } finally {
      setLoading(false);
    }
  }, [userInfoData?.token, startDate, endDate, selectedGym, selectedFollowupTime]);

  // Fetch gym visit schedule stats
  const fetchScheduleStats = useCallback(async () => {
    if (!userInfoData?.token) return;

    setScheduleLoading(true);
    try {
      const params = new URLSearchParams({ date_from: startDate, date_to: endDate });
      if (selectedGym?.value) {
        params.append("gym_id", selectedGym.value);
      }

      const response = await fetch(
        `${BASE_URL}/leads/gym-visit-schedule-stats?${params.toString()}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${userInfoData.token}` },
        }
      );

      const result = await response.json();
      if (result?.status && result?.data) {
        setScheduleStats(result.data);
      } else {
        setScheduleStats(null);
      }
    } catch (error) {
      console.error("Error fetching schedule stats:", error);
      setScheduleStats(null);
    } finally {
      setScheduleLoading(false);
    }
  }, [userInfoData?.token, startDate, endDate, selectedGym]);

  // Fetch data on filter change
  useEffect(() => {
    fetchLeadsData();
    fetchScheduleStats();
  }, [fetchLeadsData, fetchScheduleStats]);

  // Process data for hourly chart - Scheduled Visits
  const processHourlyData = useCallback(() => {
    const hourlyCount = {};
    for (let i = 6; i <= 22; i++) {
      hourlyCount[i] = 0;
    }

    leadsData.forEach((lead) => {
      const dateAdded = lead.date_added || lead.rawlead_added_date;
      if (dateAdded) {
        const timePart = dateAdded.split(" ")[1];
        if (timePart) {
          const hour = parseInt(timePart.split(":")[0], 10);
          if (hourlyCount[hour] !== undefined) {
            hourlyCount[hour]++;
          } else {
            hourlyCount[hour] = 1;
          }
        }
      }
    });

    return hourlyCount;
  }, [leadsData]);

  // Process data for hourly chart - Visit Done (from followup_details)
  const processVisitDoneData = useCallback(() => {
    const hourlyCount = {};
    for (let i = 6; i <= 22; i++) {
      hourlyCount[i] = 0;
    }

    leadsData.forEach((lead) => {
      // Look for visit_done entry in followup_details
      const followupDetails = lead.followup_details || [];
      const visitDoneEntry = followupDetails.find(
        (f) => f.follow_up_status === "visit_done"
      );

      if (visitDoneEntry) {
        // Use scheduled_on or date_added from the visit_done entry
        const visitTime = visitDoneEntry.scheduled_on || visitDoneEntry.date_added;
        if (visitTime) {
          const timePart = visitTime.split(" ")[1];
          if (timePart) {
            const hour = parseInt(timePart.split(":")[0], 10);
            if (hourlyCount[hour] !== undefined) {
              hourlyCount[hour]++;
            } else {
              hourlyCount[hour] = 1;
            }
          }
        }
      }
    });

    return hourlyCount;
  }, [leadsData]);

  // Process data for gym-wise distribution
  const processGymWiseData = useCallback(() => {
    const gymCount = {};
    const gymDetails = {};

    leadsData.forEach((lead) => {
      const gymId = lead.gym_id || "unknown";
      const gymName = lead.gym_name || "Unknown Gym";

      if (!gymCount[gymId]) {
        gymCount[gymId] = 0;
        gymDetails[gymId] = {
          gym_id: gymId,
          gym_name: gymName,
          total_visits: 0,
          converted: 0,
          pending: 0,
          leads: [],
        };
      }

      gymCount[gymId]++;
      gymDetails[gymId].total_visits++;
      gymDetails[gymId].leads.push(lead);

      if (lead.lead_status === "converted" || lead.lead_status === "closed") {
        gymDetails[gymId].converted++;
      } else {
        gymDetails[gymId].pending++;
      }
    });

    const sorted = Object.values(gymDetails).sort(
      (a, b) => b.total_visits - a.total_visits
    );

    return sorted;
  }, [leadsData]);

  // Calculate statistics - using API leadValues directly
  const stats = useMemo(() => {
    const hourlyData = processHourlyData();

    // Use values directly from API response
    const totalVisits = parseInt(leadValues?.totalMembers || 0, 10);
    const converted = parseInt(leadValues?.total_converted || 0, 10);
    const pending = totalVisits - converted;
    const uniqueGyms = new Set(leadsData.map((l) => l.gym_id)).size;

    // Peak hour calculation from local data (this is fine as it's display-only)
    let peakHour = 0;
    let peakCount = 0;
    Object.entries(hourlyData).forEach(([hour, count]) => {
      if (count > peakCount) {
        peakCount = count;
        peakHour = parseInt(hour, 10);
      }
    });

    const peakHourFormatted =
      peakHour < 12
        ? `${peakHour} AM`
        : peakHour === 12
        ? "12 PM"
        : `${peakHour - 12} PM`;

    // Visit done count from local data (display-only for chart)
    const visitsDone = leadsData.filter((lead) => {
      const followupDetails = lead.followup_details || [];
      return followupDetails.some((f) => f.follow_up_status === "visit_done");
    }).length;

    return {
      totalVisits,
      uniqueGyms,
      peakHour: peakHourFormatted,
      peakCount,
      avgPerHour: totalVisits > 0 ? Math.round(totalVisits / 17) : 0,
      converted,
      pending,
      visitsDone,
      conversionRate: totalVisits > 0 ? ((converted / totalVisits) * 100).toFixed(1) : 0,
    };
  }, [leadsData, leadValues, processHourlyData]);

  const hourlyData = processHourlyData();
  const visitDoneData = processVisitDoneData();
  const gymWiseData = processGymWiseData();

  // Chart configurations
  const hourlyChartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: "70%",
        distributed: false,
      },
    },
    colors: ["#D2000B", "#00B295"],
    dataLabels: {
      enabled: true,
      style: { fontSize: "10px", fontWeight: 600 },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      markers: { radius: 12 },
    },
    xaxis: {
      categories: Object.keys(hourlyData).map((hour) => {
        const h = parseInt(hour, 10);
        return h < 12 ? `${h}AM` : h === 12 ? "12PM" : `${h - 12}PM`;
      }),
      labels: { style: { fontSize: "12px", fontWeight: 500 } },
    },
    yaxis: {
      labels: { style: { fontSize: "12px" } },
    },
    grid: {
      borderColor: "#e7e7e7",
      strokeDashArray: 4,
    },
    tooltip: {
      theme: "dark",
      shared: true,
      intersect: false,
      y: { formatter: (val) => `${val} visits` },
    },
  };

  const hourlyChartSeries = [
    {
      name: "Scheduled Visits",
      data: Object.values(hourlyData),
    },
    {
      name: "Visit Done",
      data: Object.values(visitDoneData),
    },
  ];

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Handle gym card click
  const handleGymCardClick = (gym) => {
    setSelectedGymData(gym);
    setModalOpen(true);
  };

  // Format time from datetime string
  const formatTime = (dateStr) => {
    if (!dateStr) return "—";
    const timePart = dateStr.split(" ")[1];
    if (!timePart) return "—";
    const [hours, minutes] = timePart.split(":");
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    const formattedHour = h % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  // Get status badge class
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "converted":
      case "closed":
        return "gva-status-converted";
      case "lost":
        return "gva-status-lost";
      case "followup":
      case "follow_up":
        return "gva-status-followup";
      case "visit_done":
        return "gva-status-visit-done";
      default:
        return "gva-status-pending";
    }
  };

  // Get followup status badge class
  const getFollowupStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "visit_done":
        return "gva-followup-status--success";
      case "pending":
        return "gva-followup-status--warning";
      case "reschedule":
        return "gva-followup-status--danger";
      case "closure_expected":
      case "closure expected":
        return "gva-followup-status--info";
      case "completed":
        return "gva-followup-status--success";
      default:
        return "gva-followup-status--default";
    }
  };

  // Format datetime for followup timeline
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const parts = dateStr.split(" ");
    const datePart = parts[0];
    const timePart = parts[1];

    const date = new Date(datePart);
    const formattedDate = date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });

    if (!timePart) return formattedDate;

    const [hours, minutes] = timePart.split(":");
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    const formattedHour = h % 12 || 12;

    return `${formattedDate}, ${formattedHour}:${minutes} ${ampm}`;
  };

  // State for expanded lead details
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [leadDetailsCache, setLeadDetailsCache] = useState({});
  const [leadDetailsLoading, setLeadDetailsLoading] = useState({});

  // State for modal status filter
  const [modalStatusFilter, setModalStatusFilter] = useState("all");

  // Fetch lead followup + subscription details on demand
  const fetchLeadDetails = useCallback(async (leadId, mobile) => {
    if (!userInfoData?.token || leadDetailsCache[leadId]) return;

    setLeadDetailsLoading((prev) => ({ ...prev, [leadId]: true }));
    try {
      const [followupRes, subscriptionRes] = await Promise.all([
        fetch(`${BASE_URL}/leads/follow-up/v2?lead_id=${leadId}`, {
          headers: { Authorization: `Bearer ${userInfoData.token}` },
        }),
        mobile
          ? fetch(`${BASE_URL}/subscription/getbymobile/allmembership?mobile=${mobile}`, {
              headers: { Authorization: `Bearer ${userInfoData.token}` },
            })
          : Promise.resolve(null),
      ]);

      const followupData = await followupRes.json();
      const subscriptionData = subscriptionRes ? await subscriptionRes.json() : null;

      setLeadDetailsCache((prev) => ({
        ...prev,
        [leadId]: {
          followups: followupData?.status ? (followupData.data || []) : [],
          subscriptions: subscriptionData?.status ? (subscriptionData.data || []) : [],
          memberDetails: subscriptionData?.memberDetails || null,
        },
      }));
    } catch (error) {
      console.error("Error fetching lead details:", error);
      setLeadDetailsCache((prev) => ({
        ...prev,
        [leadId]: { followups: [], subscriptions: [], memberDetails: null },
      }));
    } finally {
      setLeadDetailsLoading((prev) => ({ ...prev, [leadId]: false }));
    }
  }, [userInfoData?.token, leadDetailsCache]);

  // Toggle lead expansion and fetch details
  const toggleLeadExpansion = (leadId, mobile) => {
    if (expandedLeadId === leadId) {
      setExpandedLeadId(null);
    } else {
      setExpandedLeadId(leadId);
      fetchLeadDetails(leadId, mobile);
    }
  };

  // Filter leads based on modal status filter
  const getFilteredLeads = (leads) => {
    if (!leads || modalStatusFilter === "all") return leads;

    return leads.filter((lead) => {
      const status = lead.lead_status?.toLowerCase();
      switch (modalStatusFilter) {
        case "pending":
          return !status || status === "pending" || status === "active" || status === "follow_up" || status === "followup";
        case "converted":
          return status === "converted" || status === "closed";
        case "visit_done":
          const followupDetails = lead.followup_details || [];
          return followupDetails.some((f) => f.follow_up_status === "visit_done");
        default:
          return true;
      }
    });
  };

  // Reset modal filter when modal closes
  const handleCloseModalWithReset = () => {
    setModalOpen(false);
    setSelectedGymData(null);
    setModalStatusFilter("all");
    setExpandedLeadId(null);
    setLeadDetailsCache({});
  };

  return (
    <HelmetWrapper title="WTF: Gym Visit Analytics">
      {/* Gym Detail Modal */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={handleCloseModalWithReset}
        contentLabel="Gym Visit Details"
        ariaHideApp={false}
      >
        <div className="createNewCTOModalContainer mod-width-70vw mod-height-80vh">
          <ModalHeadAndClose
            title={`${selectedGymData?.gym_name || "Gym"} - Scheduled Visits`}
            onClose={handleCloseModalWithReset}
          />
          <div className="gva-modal-body">
            {/* Gym Stats Summary */}
            <div className="gva-modal-stats">
              <div className="gva-modal-stat">
                <span className="gva-modal-stat-value">
                  {selectedGymData?.scheduled || 0}
                </span>
                <span className="gva-modal-stat-label">Scheduled</span>
              </div>
              <div className="gva-modal-stat gva-modal-stat--success">
                <span className="gva-modal-stat-value">
                  {selectedGymData?.visit_done || 0}
                </span>
                <span className="gva-modal-stat-label">Visit Done</span>
              </div>
              <div className="gva-modal-stat gva-modal-stat--info">
                <span className="gva-modal-stat-value">
                  {selectedGymData?.converted || 0}
                </span>
                <span className="gva-modal-stat-label">Converted</span>
              </div>
              <div className="gva-modal-stat gva-modal-stat--warning">
                <span className="gva-modal-stat-value">
                  {selectedGymData?.pending || 0}
                </span>
                <span className="gva-modal-stat-label">Pending</span>
              </div>
            </div>

            {/* Status Filter Buttons */}
            <div className="gva-modal-filters">
              <button
                className={`gva-filter-btn ${modalStatusFilter === "all" ? "is-active" : ""}`}
                onClick={() => setModalStatusFilter("all")}
              >
                All ({selectedGymData?.leads?.length || 0})
              </button>
              <button
                className={`gva-filter-btn gva-filter-btn--success ${modalStatusFilter === "visit_done" ? "is-active" : ""}`}
                onClick={() => setModalStatusFilter("visit_done")}
              >
                Visit Done ({selectedGymData?.visit_done || 0})
              </button>
              <button
                className={`gva-filter-btn gva-filter-btn--info ${modalStatusFilter === "converted" ? "is-active" : ""}`}
                onClick={() => setModalStatusFilter("converted")}
              >
                Converted ({selectedGymData?.converted || 0})
              </button>
              <button
                className={`gva-filter-btn gva-filter-btn--warning ${modalStatusFilter === "pending" ? "is-active" : ""}`}
                onClick={() => setModalStatusFilter("pending")}
              >
                Pending ({selectedGymData?.pending || 0})
              </button>
            </div>

            {/* Leads Table */}
            <div className="gva-modal-leads-table-wrapper">
              <table className="gva-modal-leads-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Assigned To</th>
                    <th>Scheduled Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const leads = selectedGymData?.leads || [];
                    const filtered = modalStatusFilter === "all"
                      ? leads
                      : modalStatusFilter === "visit_done"
                      ? leads.filter((l) => l.is_visit_done)
                      : modalStatusFilter === "converted"
                      ? leads.filter((l) => l.is_converted)
                      : modalStatusFilter === "pending"
                      ? leads.filter((l) => !l.is_visit_done)
                      : leads;

                    return filtered.length > 0 ? (
                      filtered.map((lead, idx) => {
                        const isExpanded = expandedLeadId === lead.lead_id;
                        const details = leadDetailsCache[lead.lead_id];
                        const isLoading = leadDetailsLoading[lead.lead_id];

                        return (
                          <React.Fragment key={lead.lead_id || idx}>
                            <tr
                              className={`gva-lead-row-clickable ${isExpanded ? "gva-lead-row-expanded" : ""}`}
                              onClick={() => toggleLeadExpansion(lead.lead_id, lead.mobile)}
                            >
                              <td>{idx + 1}</td>
                              <td>
                                <div className="gva-modal-lead-name">
                                  <div className="gva-modal-lead-avatar">
                                    {(lead.name || "U").charAt(0).toUpperCase()}
                                  </div>
                                  {lead.name || "Unknown"}
                                  <span className={`gva-expand-icon ${isExpanded ? "gva-expand-icon--open" : ""}`}>
                                    &#9662;
                                  </span>
                                </div>
                              </td>
                              <td>{lead.mobile || "—"}</td>
                              <td>{lead.assigned_to_name || "—"}</td>
                              <td>{formatTime(lead.next_followup_date)}</td>
                              <td>
                                {lead.is_converted ? (
                                  <span className="gva-schedule-badge gva-schedule-badge--converted">Converted</span>
                                ) : lead.is_visit_done ? (
                                  <span className="gva-schedule-badge gva-schedule-badge--done">Visit Done</span>
                                ) : (
                                  <span className="gva-schedule-badge gva-schedule-badge--pending">Pending</span>
                                )}
                              </td>
                            </tr>

                            {/* Expanded Detail Row */}
                            {isExpanded && (
                              <tr className="gva-lead-detail-row">
                                <td colSpan="6">
                                  {isLoading ? (
                                    <div className="gva-detail-loading">
                                      <div className="gva-detail-spinner" />
                                      <span>Loading details...</span>
                                    </div>
                                  ) : details ? (
                                    <div className="gva-detail-panels">
                                      {/* Followup Timeline */}
                                      <div className="gva-detail-panel">
                                        <h4 className="gva-detail-panel-title">Followup Timeline</h4>
                                        {details.followups.length > 0 ? (
                                          <div className="gva-followup-timeline">
                                            {details.followups.slice(0, 10).map((fu, fIdx) => (
                                              <div key={fIdx} className="gva-followup-item">
                                                <div className="gva-followup-dot-line">
                                                  <div className={`gva-followup-dot ${getFollowupStatusClass(fu.follow_up_status)}`} />
                                                  {fIdx < details.followups.slice(0, 10).length - 1 && (
                                                    <div className="gva-followup-line" />
                                                  )}
                                                </div>
                                                <div className="gva-followup-content">
                                                  <div className="gva-followup-header">
                                                    <span className={`gva-followup-status ${getFollowupStatusClass(fu.follow_up_status)}`}>
                                                      {(fu.follow_up_status || "unknown").replace(/_/g, " ")}
                                                    </span>
                                                    <span className="gva-followup-date">
                                                      {formatDateTime(fu.scheduled_on || fu.date_added)}
                                                    </span>
                                                  </div>
                                                  {fu.remarks && (
                                                    <p className="gva-followup-remarks">{fu.remarks}</p>
                                                  )}
                                                  <div className="gva-followup-meta">
                                                    {fu.added_by_name && (
                                                      <span className="gva-followup-by">By: {fu.added_by_name}</span>
                                                    )}
                                                    {fu.next_follow_up_date && (
                                                      <span className="gva-followup-next">Next: {formatDateTime(fu.next_follow_up_date)}</span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <p className="gva-detail-empty">No followup history</p>
                                        )}
                                      </div>

                                      {/* Subscription Details */}
                                      <div className="gva-detail-panel">
                                        <h4 className="gva-detail-panel-title">Subscription Details</h4>
                                        {details.subscriptions.length > 0 ? (
                                          <div className="gva-subscription-list">
                                            {details.subscriptions.map((sub, sIdx) => (
                                              <div key={sIdx} className="gva-subscription-card">
                                                <div className="gva-subscription-header">
                                                  <span className="gva-subscription-plan">{sub.plan_name || sub.addon_name || "Plan"}</span>
                                                  <span className={`gva-subscription-type ${
                                                    sub.type === "regular" || sub.type === "addon_pt" ? "gva-subscription-type--active" : "gva-subscription-type--expired"
                                                  }`}>
                                                    {sub.type === "addon_pt" ? "PT" : (sub.type || "—").toUpperCase()}
                                                  </span>
                                                </div>
                                                <div className="gva-subscription-details">
                                                  <div className="gva-subscription-row">
                                                    <span className="gva-subscription-label">Duration</span>
                                                    <span>
                                                      {sub.start_date ? moment(sub.start_date).format("DD MMM YY") : "—"}
                                                      {" → "}
                                                      {sub.expire_date ? moment(sub.expire_date).format("DD MMM YY") : "—"}
                                                    </span>
                                                  </div>
                                                  {sub.gym_name && (
                                                    <div className="gva-subscription-row">
                                                      <span className="gva-subscription-label">Gym</span>
                                                      <span>{sub.gym_name}</span>
                                                    </div>
                                                  )}
                                                  {sub.trainer_name && (
                                                    <div className="gva-subscription-row">
                                                      <span className="gva-subscription-label">Trainer</span>
                                                      <span>{sub.trainer_name}</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <p className="gva-detail-empty">No subscriptions found</p>
                                        )}
                                      </div>
                                    </div>
                                  ) : null}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="gva-modal-no-data">No leads found</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal>

      <Container className="gva-container" fluid>
        {/* Header */}
        <PageHeadingNew
          heading="Gym Visit Analytics"
          subheading="Real-time gym walk-in visits and analytics"
          onReload={fetchLeadsData}
        />

        <div className="mt30"></div>

        {/* Filter Section */}
        <section className="mt30">
          <FilterContainer onReset={() => { setStartDate(currentDate); setEndDate(currentDate); setSelectedGym(null); }}>
            <DateRangerFilter
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateChange}
            />
            {isAdmin && (
              <GymFilter
                selectedGym={selectedGym}
                setSelectedGym={setSelectedGym}
              />
            )}
          </FilterContainer>

          {/* NOTE: Time slot filter commented out - not used by schedule stats API */}
          {/* <div
            className="mr-timeslot__scroller"
            tabIndex={0}
            aria-label="Available time slots"
          >
            <div className="mr-timeslot__list">
              {timeSlotsQuarter.map((slot, index) => {
                const isSelected = selectedFollowupTime.some(
                  (selected) => selected.value === slot.value
                );

                return (
                  <button
                    key={index}
                    className={`mr-timeslot__btn ${isSelected ? "is-selected" : ""}`}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => handleSlotClick(slot)}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </div>
          </div> */}
        </section>

        {/* Schedule Overview Section */}
        {scheduleLoading ? (
          <div className="gva-skeleton-section mt30">
            {/* Title bar skeleton */}
            <div className="gva-skeleton-title-row">
              <div className="gva-skeleton gva-skeleton--title" />
              <div className="gva-skeleton gva-skeleton--badge" />
            </div>

            {/* 4 stat cards skeleton */}
            <div className="gva-skeleton-cards">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="gva-skeleton-card">
                  <div className="gva-skeleton gva-skeleton--icon" />
                  <div className="gva-skeleton-card-text">
                    <div className="gva-skeleton gva-skeleton--value" />
                    <div className="gva-skeleton gva-skeleton--label" />
                    <div className="gva-skeleton gva-skeleton--sub" />
                  </div>
                </div>
              ))}
            </div>

            {/* Progress bar skeleton */}
            <div className="gva-skeleton-progress-wrap">
              <div className="gva-skeleton-progress-header">
                <div className="gva-skeleton gva-skeleton--text-sm" />
                <div className="gva-skeleton gva-skeleton--text-sm gva-skeleton--short" />
              </div>
              <div className="gva-skeleton gva-skeleton--progress-bar" />
              <div className="gva-skeleton-progress-legend">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="gva-skeleton-legend-item">
                    <div className="gva-skeleton gva-skeleton--dot" />
                    <div className="gva-skeleton gva-skeleton--legend-text" />
                  </div>
                ))}
              </div>
            </div>

            {/* Table skeleton */}
            <div className="gva-skeleton-table">
              <div className="gva-skeleton-table-header">
                {[1, 2, 1, 1, 1, 2].map((w, i) => (
                  <div key={i} className="gva-skeleton gva-skeleton--th" style={{ flex: w }} />
                ))}
              </div>
              {[0, 1, 2, 3, 4].map((row) => (
                <div key={row} className="gva-skeleton-table-row">
                  {[2, 1, 1, 1, 1, 1.5].map((w, i) => (
                    <div key={i} className="gva-skeleton gva-skeleton--td" style={{ flex: w }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : scheduleStats?.summary ? (
          <div className="gva-schedule-section mt30">
            <h3 className="gva-schedule-title">
              Visit Schedule Overview
              <span className="gva-schedule-date">{formatDateDisplay(startDate)}{startDate !== endDate ? ` — ${formatDateDisplay(endDate)}` : ""}</span>
            </h3>
            <div className="gva-schedule-cards">
              <div className="gva-schedule-card gva-schedule-card--scheduled">
                <div className="gva-schedule-card-icon">
                  <ClockIcon />
                </div>
                <div className="gva-schedule-card-content">
                  <div className="gva-schedule-card-value">
                    {scheduleStats.summary.total_scheduled || 0}
                  </div>
                  <div className="gva-schedule-card-label">Scheduled</div>
                  <div className="gva-schedule-card-sub">Expected visits today</div>
                </div>
              </div>

              <div className="gva-schedule-card gva-schedule-card--done">
                <div className="gva-schedule-card-icon">
                  <VisitDoneIcon />
                </div>
                <div className="gva-schedule-card-content">
                  <div className="gva-schedule-card-value">
                    {scheduleStats.summary.total_visit_done || 0}
                  </div>
                  <div className="gva-schedule-card-label">Visit Done</div>
                  <div className="gva-schedule-card-sub">
                    {scheduleStats.summary.total_scheduled > 0
                      ? `${((scheduleStats.summary.total_visit_done / scheduleStats.summary.total_scheduled) * 100).toFixed(0)}% completion`
                      : "No visits scheduled"}
                  </div>
                </div>
              </div>

              <div className="gva-schedule-card gva-schedule-card--pending">
                <div className="gva-schedule-card-icon">
                  <PendingIcon />
                </div>
                <div className="gva-schedule-card-content">
                  <div className="gva-schedule-card-value">
                    {scheduleStats.summary.total_pending || 0}
                  </div>
                  <div className="gva-schedule-card-label">Pending</div>
                  <div className="gva-schedule-card-sub">Yet to visit</div>
                </div>
              </div>

              <div className="gva-schedule-card gva-schedule-card--converted">
                <div className="gva-schedule-card-icon">
                  <ConvertedIcon />
                </div>
                <div className="gva-schedule-card-content">
                  <div className="gva-schedule-card-value">
                    {scheduleStats.summary.total_converted || 0}
                  </div>
                  <div className="gva-schedule-card-label">Converted</div>
                  <div className="gva-schedule-card-sub">
                    {scheduleStats.summary.total_visit_done > 0
                      ? `${((scheduleStats.summary.total_converted / scheduleStats.summary.total_visit_done) * 100).toFixed(0)}% of visited`
                      : "From visit done"}
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            {scheduleStats.summary.total_scheduled > 0 && (
              <div className="gva-schedule-progress">
                <div className="gva-schedule-progress-header">
                  <span>Completion Progress</span>
                  <span className="gva-schedule-progress-pct">
                    {((scheduleStats.summary.total_visit_done / scheduleStats.summary.total_scheduled) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="gva-schedule-progress-track">
                  <div
                    className="gva-schedule-progress-fill gva-schedule-progress-fill--converted"
                    style={{
                      width: `${((scheduleStats.summary.total_converted || 0) / scheduleStats.summary.total_scheduled) * 100}%`,
                    }}
                  />
                  <div
                    className="gva-schedule-progress-fill gva-schedule-progress-fill--done"
                    style={{
                      width: `${((scheduleStats.summary.total_visit_done_not_converted || 0) / scheduleStats.summary.total_scheduled) * 100}%`,
                    }}
                  />
                  <div
                    className="gva-schedule-progress-fill gva-schedule-progress-fill--pending"
                    style={{
                      width: `${(scheduleStats.summary.total_pending / scheduleStats.summary.total_scheduled) * 100}%`,
                    }}
                  />
                </div>
                <div className="gva-schedule-progress-legend">
                  <span className="gva-schedule-legend-item">
                    <span className="gva-schedule-legend-dot gva-schedule-legend-dot--converted" />
                    Converted ({scheduleStats.summary.total_converted || 0})
                  </span>
                  <span className="gva-schedule-legend-item">
                    <span className="gva-schedule-legend-dot gva-schedule-legend-dot--done" />
                    Visit Done ({scheduleStats.summary.total_visit_done_not_converted || 0})
                  </span>
                  <span className="gva-schedule-legend-item">
                    <span className="gva-schedule-legend-dot gva-schedule-legend-dot--pending" />
                    Pending ({scheduleStats.summary.total_pending})
                  </span>
                </div>
              </div>
            )}

            {/* Gym-wise Schedule Breakdown */}
            {scheduleStats.gym_breakdown?.length > 0 && (
              <div className="gva-schedule-gym-table-wrapper">
                <table className="gva-schedule-gym-table">
                  <thead>
                    <tr>
                      <th>Gym</th>
                      <th>Scheduled</th>
                      <th>Visit Done</th>
                      <th>Converted</th>
                      <th>Pending</th>
                      <th>Completion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleStats.gym_breakdown.map((gym) => (
                      <tr
                        key={gym.gym_id}
                        className="gva-schedule-row-clickable"
                        onClick={() => {
                          setSelectedGymData({
                            gym_id: gym.gym_id,
                            gym_name: gym.gym_name,
                            scheduled: gym.scheduled,
                            visit_done: gym.visit_done,
                            converted: gym.converted || 0,
                            pending: gym.pending,
                            leads: gym.leads || [],
                          });
                          setModalOpen(true);
                        }}
                      >
                        <td className="gva-schedule-gym-name">{gym.gym_name || "Unknown"}</td>
                        <td>{gym.scheduled}</td>
                        <td>
                          <span className="gva-schedule-badge gva-schedule-badge--done">
                            {gym.visit_done}
                          </span>
                        </td>
                        <td>
                          <span className="gva-schedule-badge gva-schedule-badge--converted">
                            {gym.converted || 0}
                          </span>
                        </td>
                        <td>
                          <span className="gva-schedule-badge gva-schedule-badge--pending">
                            {gym.pending}
                          </span>
                        </td>
                        <td>
                          <div className="gva-schedule-mini-progress">
                            <div
                              className="gva-schedule-mini-fill"
                              style={{
                                width: `${gym.scheduled > 0 ? (gym.visit_done / gym.scheduled) * 100 : 0}%`,
                              }}
                            />
                            <span className="gva-schedule-mini-text">
                              {gym.scheduled > 0
                                ? `${((gym.visit_done / gym.scheduled) * 100).toFixed(0)}%`
                                : "0%"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {/* NOTE: Below section commented out - was showing /leads/v2 walk-in data,
            now replaced by schedule overview above using /leads/gym-visit-schedule-stats */}
        {/* {loading ? (
          <div className="mt30">
            <CalendarLoader daysCount={6} shiftsPerDay={2} />
          </div>
        ) : (
          <>
            <div className="mr-kpi-grid mt30">
              <div className="mr-kpi-card">
                <div className="mr-kpi-head">
                  <div>
                    <p className="mr-kpi-title">Total Visits</p>
                    <div className="mr-kpi-value-row">
                      <div className="mr-kpi-value">{stats.totalVisits}</div>
                    </div>
                    <div className="mr-kpi-subtitle">
                      {formatDateDisplay(startDate)}{startDate !== endDate ? ` — ${formatDateDisplay(endDate)}` : ""}
                    </div>
                  </div>
                  <span className="mr-kpi-icon mr-kpi-icon--info">
                    <VisitorsIcon />
                  </span>
                </div>
              </div>

              <div className="mr-kpi-card">
                <div className="mr-kpi-head">
                  <div>
                    <p className="mr-kpi-title">Gyms Active</p>
                    <div className="mr-kpi-value-row">
                      <div className="mr-kpi-value">{stats.uniqueGyms}</div>
                    </div>
                    <div className="mr-kpi-subtitle">with walk-ins</div>
                  </div>
                  <span className="mr-kpi-icon mr-kpi-icon--success">
                    <GymsIcon />
                  </span>
                </div>
              </div>

              <div className="mr-kpi-card">
                <div className="mr-kpi-head">
                  <div>
                    <p className="mr-kpi-title">Peak Hour</p>
                    <div className="mr-kpi-value-row">
                      <div className="mr-kpi-value">{stats.peakHour}</div>
                    </div>
                    <div className="mr-kpi-subtitle">
                      {stats.peakCount} visits
                    </div>
                  </div>
                  <span className="mr-kpi-icon mr-kpi-icon--warning">
                    <ClockIcon />
                  </span>
                </div>
              </div>

              <div className="mr-kpi-card">
                <div className="mr-kpi-head">
                  <div>
                    <p className="mr-kpi-title">Avg / Hour</p>
                    <div className="mr-kpi-value-row">
                      <div className="mr-kpi-value">{stats.avgPerHour}</div>
                    </div>
                    <div className="mr-kpi-subtitle">visits per hour</div>
                  </div>
                  <span className="mr-kpi-icon mr-kpi-icon--info">
                    <TrendingIcon />
                  </span>
                </div>
              </div>

              <div className="mr-kpi-card">
                <div className="mr-kpi-head">
                  <div>
                    <p className="mr-kpi-title">Converted</p>
                    <div className="mr-kpi-value-row">
                      <div className="mr-kpi-value">{stats.converted}</div>
                      {stats.totalVisits > 0 && (
                        <div className="mr-kpi-trend mr-kpi-trend--up">
                          {stats.conversionRate}%
                        </div>
                      )}
                    </div>
                    <div className="mr-kpi-subtitle">closed leads</div>
                  </div>
                  <span className="mr-kpi-icon mr-kpi-icon--success">
                    <ConvertedIcon />
                  </span>
                </div>
              </div>

              <div className="mr-kpi-card">
                <div className="mr-kpi-head">
                  <div>
                    <p className="mr-kpi-title">Pending</p>
                    <div className="mr-kpi-value-row">
                      <div className="mr-kpi-value">{stats.pending}</div>
                    </div>
                    <div className="mr-kpi-subtitle">awaiting follow-up</div>
                  </div>
                  <span className="mr-kpi-icon mr-kpi-icon--danger">
                    <PendingIcon />
                  </span>
                </div>
              </div>

              <div className="mr-kpi-card">
                <div className="mr-kpi-head">
                  <div>
                    <p className="mr-kpi-title">Visit Done</p>
                    <div className="mr-kpi-value-row">
                      <div className="mr-kpi-value">{stats.visitsDone}</div>
                      {stats.totalVisits > 0 && (
                        <div className="mr-kpi-trend mr-kpi-trend--up">
                          {((stats.visitsDone / stats.totalVisits) * 100).toFixed(1)}%
                        </div>
                      )}
                    </div>
                    <div className="mr-kpi-subtitle">completed today</div>
                  </div>
                  <span className="mr-kpi-icon mr-kpi-icon--success">
                    <VisitDoneIcon />
                  </span>
                </div>
              </div>
            </div>

            <div className="gva-chart-section mt30">
              <div className="mr-kpi-card">
                <h3 className="gva-chart-title">Scheduled vs Visit Done</h3>
                <p className="gva-chart-subtitle">Hourly comparison of scheduled visits and completed visits</p>
                {stats.totalVisits > 0 ? (
                  <ReactApexChart
                    options={hourlyChartOptions}
                    series={hourlyChartSeries}
                    type="bar"
                    height={350}
                  />
                ) : (
                  <div className="gva-no-data">
                    <ClockIcon />
                    <p>No visit data available for the selected filters</p>
                  </div>
                )}
              </div>
            </div>

            {gymWiseData.length > 0 && (
              <section className="mr-gc-grid mt30 mb30" aria-label="Gym locations">
                {gymWiseData.slice(0, 12).map((gym) => (
                  <article
                    key={gym.gym_id}
                    className="mr-gc-card"
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${gym.gym_name} details`}
                    onClick={() => handleGymCardClick(gym)}
                    onKeyDown={(e) => e.key === "Enter" && handleGymCardClick(gym)}
                  >
                    <header className="mr-gc-head">
                      <div className="mr-gc-head-left">
                        <h3 className="mr-gc-title">{gym.gym_name}</h3>
                        <div className="mr-gc-subrow">
                          <span className="mr-badge mr-badge--secondary">
                            {gym.total_visits} visits
                          </span>
                        </div>
                      </div>
                      <div className="mr-gc-chevron">
                        <ChevronRightIcon />
                      </div>
                    </header>

                    <div className="mr-gc-two-up">
                      <div className="mr-gc-stat">
                        <div className="mr-gc-stat-label">
                          <div className="mr-gc-qicon mr-gc-qicon--success">
                            <ConvertedIcon />
                          </div>
                          <span>Converted</span>
                        </div>
                        <div className="mr-gc-stat-value">{gym.converted}</div>
                      </div>
                      <div className="mr-gc-stat">
                        <div className="mr-gc-stat-label">
                          <div className="mr-gc-qicon mr-gc-qicon--warning">
                            <PendingIcon />
                          </div>
                          <span>Pending</span>
                        </div>
                        <div className="mr-gc-stat-value">{gym.pending}</div>
                      </div>
                    </div>

                    <div className="mr-gc-util" aria-label="Conversion rate">
                      <div className="mr-gc-util-row">
                        <span className="mr-gc-util-label">Conversion Rate</span>
                        <span
                          className={`mr-gc-util-rate is-${
                            gym.total_visits > 0
                              ? (gym.converted / gym.total_visits) * 100 >= 50
                                ? "good"
                                : (gym.converted / gym.total_visits) * 100 >= 25
                                ? "warn"
                                : "bad"
                              : "bad"
                          }`}
                        >
                          {gym.total_visits > 0
                            ? ((gym.converted / gym.total_visits) * 100).toFixed(0)
                            : 0}
                          %
                        </span>
                      </div>
                      <div className="mr-gc-util-track" aria-hidden="true">
                        <div
                          className={`mr-gc-util-fill is-${
                            gym.total_visits > 0
                              ? (gym.converted / gym.total_visits) * 100 >= 50
                                ? "good"
                                : (gym.converted / gym.total_visits) * 100 >= 25
                                ? "warn"
                                : "bad"
                              : "bad"
                          }`}
                          style={{
                            width: `${
                              gym.total_visits > 0
                                ? (gym.converted / gym.total_visits) * 100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </article>
                ))}
              </section>
            )}
          </>
        )} */}
      </Container>
    </HelmetWrapper>
  );
};

export default GymVisitAnalytics;